import React from "react";
import { StaticQuery, graphql } from "gatsby";
import { getImage } from "gatsby-plugin-image";
import { GatsbyImage } from "./gatsby-image";
import RoofingCalculator from "./sidebars/roof-calc";
import cityPageContent from "../data/city-page-content";

/**
 * `section` picks which half renders. The design orders these as
 * costs -> "Get A New Roof In 3 Steps" -> permits, so the two blocks have to be
 * placeable independently. "all" keeps the original combined order.
 */
const WyomingCostsAndPermits = ({ city = "Wyoming", section = "all" }) => (
  <StaticQuery
    query={graphql`
      query {
        # Tile-roof photo from the Wyoming design (Figma "image 49"), extracted
        # from the supplied SVG export.
        costsImage: file(relativePath: { eq: "wyoming-tile-roof.jpg" }) {
          childImageSharp {
            gatsbyImageData(width: 800)
          }
        }
        permitImage: file(relativePath: { eq: "roofing_bg.jpg" }) {
          childImageSharp {
            gatsbyImageData(width: 800)
          }
        }
      }
    `}
    render={(data) => {
      const costsImg = getImage(data.costsImage.childImageSharp);
      const permitImg = getImage(data.permitImage.childImageSharp);
      // Every block here is roofing copy, and service-city.js renders this on
      // the Wyoming roofing page only.
      const { costsClosing, permitsBody } = cityPageContent.forPage(city, "roofing");
      const show = (name) => section === "all" || section === name;

      return (
        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 md:space-y-20">
            {show("permits") ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-extrabold text-neutral-900 mb-6 leading-tight">
                  Do You Need Permit For Roofing In Wyoming, MI?
                </h2>
                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  {permitsBody ? (
                    <p>{permitsBody}</p>
                  ) : (
                    <>
                      <p>
                        Yes, most roofing projects in Wyoming, MI require a permit. If
                        you're doing a full roof replacement or significant repairs, the city
                        wants to make sure the work meets local building codes and
                        safety standards.
                      </p>
                      <p>
                        Minor fixes like patching a few shingles typically don't need a
                        permit. But if you're tearing off old roofing, replacing large
                        sections, or making any structural changes, you'll need one. It's
                        not just a formality. Permits protect you by ensuring the work is
                        done correctly and safely.
                      </p>
                      <p>
                        Permit fees in Wyoming are usually affordable and often built into
                        your contractor's quote. Skipping this step can cause headaches
                        later, especially if you decide to sell your home. Buyers and
                        inspectors will ask for proof that work was permitted.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="rounded-xl overflow-hidden bg-neutral-200 min-h-[260px] md:min-h-[360px] lg:min-h-[440px]">
                {permitImg ? (
                  <GatsbyImage
                    image={permitImg}
                    alt="Roofing permits in Wyoming, MI"
                    className="w-full h-full"
                  />
                ) : null}
              </div>
            </div>
            ) : null}

            {show("permits") ? (
            <div className="rounded-2xl overflow-hidden relative px-4 sm:px-8 py-12 md:py-16">
              <RoofingCalculator />
            </div>
            ) : null}

            {show("costs") ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Fixed height + cover: the source is 2:3 portrait, and left to
                  its own aspect it renders ~876px tall and swamps the column.
                  The design crops it to roughly 605x575. */}
              <div className="rounded-xl overflow-hidden bg-neutral-200 h-[320px] md:h-[440px] lg:h-[575px]">
                {costsImg ? (
                  <GatsbyImage
                    image={costsImg}
                    alt="Clay tile roof being installed on battens in Wyoming, MI"
                    className="w-full h-full"
                    imgStyle={{ objectFit: "cover" }}
                  />
                ) : null}
              </div>

              <div>
                <h2 className="text-3xl md:text-4xl font-display font-extrabold text-neutral-900 mb-6 leading-tight">
                  Roofing Costs In Wyoming, MI
                </h2>
                <div className="space-y-4 text-neutral-700 leading-relaxed">
                  <p>
                    Wyoming, MI, sees roofing costs fluctuate according to the type of
                    materials used, the size of the roof, and how simple or complex the
                    roofing job is. Homeowners there can expect to pay somewhere
                    between <strong>$8,500</strong> and <strong>$14,500</strong> for a
                    standard asphalt shingle replacement on a house that's 1,700–2,000
                    square feet (whose costs run about <strong>$4.50</strong> to{" "}
                    <strong>$7.50</strong> per sq. ft.), with the total increased by
                    the need to perform such tasks as taking off the old shingles, making
                    repairs to the decking, and upgrading the ventilation.
                  </p>
                  <p>
                    Premium asphalt and architectural shingles often land in the{" "}
                    <strong>$7–$10</strong>+ per sq. ft. range, while metal roofing can run{" "}
                    <strong>$10–$18</strong> per sq. ft. depending on the profile. Flat or
                    low-slope systems typically come in around $5–$9 per sq. ft. Small
                    repairs in Kent County generally range from $250–$1,500 based on leak
                    location and access. Local factors like permit and dumpster fees,
                    steep pitches, skylights, and seasonal demand can influence pricing.
                  </p>
                  {costsClosing ? <p>{costsClosing}</p> : null}
                </div>
              </div>
            </div>
            ) : null}
          </div>
        </section>
      );
    }}
  />
);

export default WyomingCostsAndPermits;
